import React, { forwardRef } from 'react'
import { cn } from '../../utils/cn'
import { useTheme } from '../providers/ThemeProvider'

// ═══════════════════════════════════════════════════════════════════════════
// Textarea - Journal Entry Style for Diary Theme
// ═══════════════════════════════════════════════════════════════════════════

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
 className?: string
 /** Textarea variant for diary theme */
 variant?: 'default' | 'lined' | 'plain'
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
 ({ className, variant = 'default', style, ...props }, ref) => {
 const { isDiary, diaryFontFamily } = useTheme()
 
 // Diary theme styles - Journal page aesthetic
 const getDiaryStyles = (): React.CSSProperties => {
 const baseStyles: React.CSSProperties = {
 fontFamily: 'var(--font-body)',
 color: 'var(--ink-black)',
 lineHeight: '1.8',
 transition: 'all 0.2s ease',
 }
 
 switch (variant) {
 case 'lined':
 // Lined paper look
 return {
 ...baseStyles,
 fontFamily: diaryFontFamily,
 backgroundColor: 'var(--paper-cream)',
 border: '1px solid var(--paper-margin)',
 borderLeft: '3px solid var(--ink-sepia)',
 borderRadius: '4px',
 backgroundImage: 'repeating-linear-gradient(transparent, transparent 27px, var(--paper-margin) 27px, var(--paper-margin) 28px)',
 backgroundSize: '100% 28px',
 paddingTop: '7px', // Align with lines
 }
 case 'plain':
 // Clean journal page
 return {
 ...baseStyles,
 fontFamily: diaryFontFamily,
 backgroundColor: 'transparent',
 border: 'none',
 borderBottom: '1px dashed var(--ink-faded)',
 borderRadius: '0',
 }
 default:
 // Standard with paper aesthetics
 return {
 ...baseStyles,
 backgroundColor: 'var(--paper-cream)',
 border: '1px solid var(--paper-margin)',
 borderLeft: '2px solid var(--ink-faded)',
 borderRadius: '4px',
 boxShadow: 'inset 1px 1px 3px var(--shadow-color, rgba(92, 64, 51, 0.05))',
 }
 }
 }
 
 return (
 <textarea
 ref={ref}
 className={cn(
"flex min-h-[80px] w-full rounded-md px-3 py-2 text-sm",
 !isDiary && [
"border border-strong",
"bg-surface",
"placeholder:text-muted dark:placeholder:text-muted",
// 2026-08-29 UI audit: ring-offset-2 with no offset COLOUR falls back to
 // white, which drew a white halo round every focused textarea on the dark
 // themes. The offset now follows the surface token.
"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
 ],
 isDiary && [
"placeholder:text-[var(--ink-faded)]",
"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--horton-purple)] focus-visible:ring-offset-1 focus-visible:ring-offset-surface",
 ],
"disabled:cursor-not-allowed disabled:opacity-50",
"resize-none",
 className
 )}
 style={isDiary ? { ...getDiaryStyles(), ...style } : style}
 {...props}
 />
 )
 }
)
Textarea.displayName = 'Textarea'

export { Textarea }
